// Lib
import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import { useDispatch } from 'react-redux';
import { isEqual } from 'lodash';
import type { GridSettings } from 'handsontable/settings';

// Utils
import { hasCommandModifier, isControlKeyCommand } from '../../../utils/keyboard/keyboardUtility';
import { getFirstSelection, getStartCol, getStartRow } from '../utils/selectionUtils/tableSelectionAccessors';
import {
    hasSelections,
    isSelectingSingleCell,
    isSelectingSingleSelection,
} from '../utils/selectionUtils/tableSelectionPredicateUtils';
import { adjustTableContentDataSize } from '../utils/dataUtils/grid/tableGridSizingUtils';
import { getTableElementFromHotInstance } from '../utils/domUtils/hotTableDomAccessorUtils';
import HotTableClipboardSourceManager from '../manager/HotTableClipboardSourceManager';
import useRefCallback from '../../../utils/react/useRefCallback';
import { getColumnCount, getRowCount } from '../../../../common/table/utils/tableCellDataPropertyUtils';
import { buildHotTableContentData } from '../utils/hotInstanceUtils/hotTableDataUtils';
import {
    createCellDataChanges,
    createCellDataChangesFromReplay,
    createCellDataChangesFromSource,
} from '../utils/hotInstanceUtils/cell/hotTableCellEditingUtils';
import { isTableCellMetaChangeSource } from '../../../../common/table/utils/tableChangeSourceUtils';
import { setHotTableCellMetaObject } from '../utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import {
    getUndoRedoReplayCellData,
    setPendingUndoRedoActionCellDataChanges,
    type UndoRedoRichCellDataState,
} from '../utils/hotInstanceUtils/undoRedo/hotTableUndoRedoRichCellDataUtils';

// Actions
import { removeSelectedElements } from '../../selection/selectionActions';

// Types
import type { RawDraftContentState } from 'draft-js';
import { MNHotInstance } from '../tableHotInstanceTypes';
import { CellData, CellDataChange, CellValueChange, TableChangeSource } from '../../../../common/table/TableTypes';
import { findAnchorElement } from '../../../utils/dom/domEventTargetUtils';

type HotTableHook<Key extends keyof GridSettings> = NonNullable<GridSettings[Key]>;

export type UseTableCellEditingHandlersReturn = {
    beforeChange: HotTableHook<'beforeChange'>;
    beforeOnCellMouseDown: HotTableHook<'beforeOnCellMouseDown'>;
};

type HotMouseEvent = MouseEvent & {
    // Handsontable private property
    isImmediatePropagationEnabled?: boolean;
};

/**
 * Returns draft editor content only for direct editor commits.
 *
 * Handsontable replays undo/redo values through `changes`, so reusing the currently mounted editor content for
 * `UndoRedo.undo`/`UndoRedo.redo` can overwrite those replayed values with stale text from the last edited cell.
 */
export const getTableEditorDraftContentForChangeSource = (
    source: string | undefined,
    activeEditor: { content?: RawDraftContentState } | undefined,
): RawDraftContentState | undefined => {
    if (source !== 'edit') return;

    return activeEditor?.content;
};

/**
 * Custom hook for handling table cell editing operations
 */
export const useTableCellEditingHandlers = ({
    elementId,
    locale,
    currencyPreference,
    hotTableInstanceRef,
    isSingleSelected,
    undoRedoRichCellDataState,
}: {
    elementId: string;
    locale: string;
    currencyPreference: string;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
    isSingleSelected: boolean;
    undoRedoRichCellDataState: UndoRedoRichCellDataState;
}): UseTableCellEditingHandlersReturn => {
    const dispatch = useDispatch();

    const dispatchDeselectElement = useCallback(
        (elId: string) => dispatch(removeSelectedElements({ ids: [elId], transactionId: undefined })),
        [dispatch],
    );

    /**
     * Normalises incoming HOT edits into Milanote cell data before HOT commits them.
     *
     * Meta-only change sources are allowed through unchanged so existing persisted cell data stays intact. For user,
     * paste, and autofill edits, this hook rebuilds rich cell payloads, expands the grid if the change reaches beyond
     * the current bounds, rewrites HOT's pending values to the resolved cell values, and mirrors the updated cell data
     * into HOT cell meta so follow-up handlers read the same canonical state.
     */
    const beforeChange = useRefCallback<UseTableCellEditingHandlersReturn['beforeChange']>(
        (changes, source) => {
            const hotInstance = hotTableInstanceRef.current;
            if (!hotInstance || !changes?.length) return;

            // If source is coming from updating cell meta, save data as is
            if (isTableCellMetaChangeSource(source)) return true;

            const cellValueChanges = changes.filter((change): change is CellValueChange => change !== null);

            const data = buildHotTableContentData(hotInstance);

            // Convert cell changes [row, col, prevValue, newValue] to [row, col, prevCellData, newCellData]
            let cellDataChanges: CellDataChange[];

            // Step 4 of undo/redo flow
            // For undo / redo - we need to get the data that was retrieved from the undo action
            //  and apply it to the cell data changes
            if (source === TableChangeSource.UNDO || source === TableChangeSource.REDO) {
                const replayCellDataByCoordinate = new Map<string, CellData>();

                cellValueChanges.forEach(([row, col]) => {
                    const replayCellData = getUndoRedoReplayCellData(undoRedoRichCellDataState, row, Number(col));
                    if (!replayCellData) return;

                    replayCellDataByCoordinate.set(`${row}:${Number(col)}`, replayCellData);
                });

                cellDataChanges = createCellDataChangesFromReplay(
                    cellValueChanges,
                    data,
                    replayCellDataByCoordinate,
                    locale,
                    currencyPreference,
                );
            } else if (source === TableChangeSource.AUTOFILL_FILL || source === TableChangeSource.COPY_PASTE_PASTE) {
                const sourceCellDataArray =
                    source === TableChangeSource.AUTOFILL_FILL
                        ? HotTableClipboardSourceManager.getTableAutofillData()
                        : HotTableClipboardSourceManager.getTableCopiedDataForChanges(cellValueChanges);

                cellDataChanges = createCellDataChangesFromSource(
                    cellValueChanges,
                    data,
                    source,
                    sourceCellDataArray,
                    locale,
                ) as CellDataChange[];
            } else {
                // Retrieve draft editor content from the active editor
                const activeEditor = hotInstance.getActiveEditor() as { content?: RawDraftContentState } | undefined;
                const tableEditorDraftContent = getTableEditorDraftContentForChangeSource(source, activeEditor);

                cellDataChanges = createCellDataChanges(
                    cellValueChanges,
                    data,
                    locale,
                    currencyPreference,
                    tableEditorDraftContent,
                );
            }

            // Adjust table size to fit all the changes
            const nRows = getRowCount(data);
            const nCols = getColumnCount(data);

            const maxChangeRow = Math.max(...cellDataChanges.map(([row]) => row)) + 1;
            const maxChangeCol = Math.max(...cellDataChanges.map(([, col]) => Number(col))) + 1;

            const maxRow = Math.max(maxChangeRow, nRows);
            const maxCol = Math.max(maxChangeCol, nCols);

            const newData = adjustTableContentDataSize(data, maxRow, maxCol);

            const nRowsNew = getRowCount(newData) - nRows;
            const nColsNew = getColumnCount(newData) - nCols;

            if (nRowsNew > 0) {
                hotInstance.alter('insert_row_below', nRows - 1, nRowsNew, source);
            }

            if (nColsNew > 0) {
                hotInstance.alter('insert_col_end', nCols - 1, nColsNew, source);
            }

            // Apply changes if any
            cellDataChanges.forEach((cellDataChange, index) => {
                const [row, col, _previousCellData, newCellData] = cellDataChange;
                const numericCol = Number(col);

                cellValueChanges[index][3] = newCellData.value;
                newData[row][numericCol] = newCellData;

                setHotTableCellMetaObject(hotInstance, row, numericCol, { cellData: newCellData });
            });

            if (isEqual(newData, data)) return;

            // Step 1 of the undo/redo flow
            //  Store the changes associated with the HOT instance so it can be used in a future undo/redo action
            setPendingUndoRedoActionCellDataChanges(undoRedoRichCellDataState, cellDataChanges);
        },
        [currencyPreference, hotTableInstanceRef, locale, undoRedoRichCellDataState],
    );

    /**
     * Intercepts cell mouse-down interactions so embedded controls, element selection, multi-select shortcuts, and
     * single-cell re-entry into edit mode all behave like the surrounding Milanote selection model expects.
     */
    const beforeOnCellMouseDown = useRefCallback<UseTableCellEditingHandlersReturn['beforeOnCellMouseDown']>(
        (event, coords) => {
            const hotEvent = event as HotMouseEvent;
            if (hotEvent.isImmediatePropagationEnabled === false) return;

            const hotInstance = hotTableInstanceRef.current;
            if (!hotInstance) return;

            const target = event.target as HTMLElement | null;

            // If user clicks on a checkbox in a cell, don't select the cell
            if (target?.parentElement?.classList.contains('checkbox-animated')) {
                hotEvent.isImmediatePropagationEnabled = false;
            }

            // If a link has been clicked, don't select the cell
            if (findAnchorElement(target)) {
                event.stopImmediatePropagation();
                event.preventDefault();
                return false;
            }

            if (!isSingleSelected) {
                // If the table element is not yet selected, select the table element instead of the cell
                // this would only happen when the cover is not present (e.g. when the table is in mobile view)
                const tableElement = getTableElementFromHotInstance(hotInstance);
                if (!tableElement) return;

                // Both events are necessary to trigger the selection of the table element
                // In ElementContainer mouseDown alone will never select the element as there is an early
                // return for document mode, and click will only run if there has been a mouseDown event
                tableElement.dispatchEvent(new MouseEvent('mousedown', event));
                tableElement.click();
                return;
            }

            const isRightMouseClick = event.button === 2;
            if (isRightMouseClick) return;

            const hotCurrentCellSelections = hotInstance.getSelected();

            // On Cmd+click, and no cells are selected, deselect element
            if (
                (hasCommandModifier(event) || isControlKeyCommand(event)) &&
                !hasSelections(hotCurrentCellSelections || [])
            ) {
                event.stopImmediatePropagation();
                event.preventDefault();

                dispatchDeselectElement(elementId);
                return;
            }

            // On clicking the selected cell one more time, immediately go to edit mode.
            const currentSelection = getFirstSelection(hotCurrentCellSelections);
            if (!currentSelection) return;

            const isSelectingCurrentlySelectedCell =
                !!hotCurrentCellSelections &&
                isSelectingSingleSelection(hotCurrentCellSelections) &&
                isSelectingSingleCell(currentSelection) &&
                coords.row === getStartRow(currentSelection) &&
                coords.col === getStartCol(currentSelection);

            if (
                !(hasCommandModifier(event) || isControlKeyCommand(event)) &&
                isSelectingCurrentlySelectedCell &&
                hotInstance.getActiveEditor()
            ) {
                event.stopImmediatePropagation();
                event.preventDefault();

                hotInstance.getActiveEditor()?.beginEditing();
            }
        },
        [dispatchDeselectElement, elementId, hotTableInstanceRef, isSingleSelected],
    );

    return {
        beforeChange,
        beforeOnCellMouseDown,
    };
};

export default useTableCellEditingHandlers;
